import React, { useEffect, useState, useMemo, useContext, useRef } from 'react';
import { useAccount, usePublicClient, useWaitForTransactionReceipt, useWalletClient } from 'wagmi';

import { MobileContext } from 'context';
import { useGameDetails, useStateWithLocalStorage } from 'utils/Hooks';
import { CHAIN_DETAILS, MAX_ROUNDS_CLAIMALL_DEFAULT, MAX_ROUNDS_CLAIMALL_HARDCAP, ABI } from 'const';
import { ToastErrorTx, ToastProgressTx, ToastSuccessTx } from 'utils/Toasts/transaction';
import { BackButton } from 'utils/Misc';
import 'pages/History/styles.scss';
import './claimall.scss';
import { GetPredictionContractAddr } from 'context/url_based_context';


function ClaimAll(props) {
  const loadingScreenUpdatePeriod = 5; // update every 5 rounds

  const isMobile = useContext(MobileContext);
  const gameDetails = useGameDetails();
  const inputField_MaxRoundsOverride = useRef();

  const [nOfPlayedRounds, SetNumberOfPlayedRounds] = useState(null);
  const [roundsProcessedSoFar, SetRoundsProcessed] = useState(0);
  const [claimableRounds, SetClaimableRounds] = useState(null);
  const [historyOffset, SetHistoryOffset] = useState(0);
  const [maxRoundsOverride, SetMaxRoundsOverride] = useStateWithLocalStorage('max_claimall_rounds');

  const account = useAccount();
  const walletClient = useWalletClient({ chainId: account?.chainId });
  const publicClient = usePublicClient();

  const [betTxHash, SetBetTxHash] = useState(null);
  const [formattedError, SetFormattedError] = useState('');

  const {
    isSuccess: isTxConfirmed_hook, failureReason: txFailReason
  } = useWaitForTransactionReceipt({ hash: betTxHash, confirmations: 1 });


  useEffect(() => {
    DetermineTxStatusFromHooks()
  }, [isTxConfirmed_hook, betTxHash, txFailReason]);

  useEffect(() => {
    HandleTxError();
  }, [formattedError, betTxHash]);


  function DetermineTxStatusFromHooks() {
    if (isTxConfirmed_hook && betTxHash) {
      console.info(`TX CONFIRMED by hook`);
      ShowTxSuccessToast(betTxHash);
    } else if (txFailReason) {
      /*
      for (const [key, value] of Object.entries(txFailReason)) {
        console.log({ [key]: value });
      }
      */
      console.info(`TX REVERTED according to hook`);
      let reason = 'unknown error';
      const matches = txFailReason.details.match(/:(.*): 0x/);
      if (matches && matches[1])
        reason = matches[1].trim();
      else
        reason = txFailReason.shortMessage.slice(0, 40);
      SetFormattedError(`${reason}`);
    }
  }

  function ShowTxPendingToast() {
    ToastProgressTx();
  }

  function HandleTxError() {
    if (formattedError) {
      ToastErrorTx(formattedError, betTxHash);
      SetBetTxHash(null); // stop useWaitForTransactionReceipt hook from further updates
      SetFormattedError('');
    }
  }

  function ShowTxSuccessToast(_txHash) {
    if (!_txHash)
      throw new SyntaxError(`missing transaction hash when trying to show success toast`);
    ToastSuccessTx(_txHash);
    SetBetTxHash(null); // stop useWaitForTransactionReceipt hook from further updates
    SetClaimableRounds([]); // reset claimable round IDs
  }



  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    UpdateNumberOfPlayedRounds();
  }, [maxRoundsOverride, account?.address, gameDetails]);

  useEffect(() => {
    UpdateClaimableRoundIDs();
  }, [nOfPlayedRounds, account?.address, historyOffset]);
  /* eslint-enable react-hooks/exhaustive-deps */



  async function UpdateNumberOfPlayedRounds() {
    if (!account?.address || !gameDetails?.contracts.prediction) return;
    let historyLength = Number(
      await gameDetails.contracts.prediction.methods.GetUserRoundsLength(account?.address).call());
    const maxRoundsToQuery = GetMaxNumberOfRoundsToQuery();
    if (historyLength > maxRoundsToQuery) {
      const historyOffset = historyLength - maxRoundsToQuery;
      SetHistoryOffset(historyOffset);
      historyLength = maxRoundsToQuery;
    }
    SetNumberOfPlayedRounds(historyLength);
  }

  async function UpdateClaimableRoundIDs() {
    if (!account?.address || nOfPlayedRounds === null) return;
    const historyRaw = await gameDetails.contracts.prediction.methods.GetUserRounds(
      account?.address, historyOffset, nOfPlayedRounds).call();
    const roundNumbers = historyRaw[0];
    const history = historyRaw[1];
    if (history === undefined || history.length === undefined) return; // history was not fetched properly
    const roundsToBeClaimed = await IdentifyClaimableRounds(history, roundNumbers);
    SetClaimableRounds(roundsToBeClaimed);
  }

  async function IdentifyClaimableRounds(history, roundNumbers) {
    if (!history || !roundNumbers) throw new SyntaxError('missing required argument(s): history and/or roundNumbers');
    let roundsToBeClaimed = [];
    let roundsProcessed = 0;
    for (let thisRound = 0; thisRound < history.length; thisRound++) {
      if (!history[thisRound].claimed) {
        const roundID = roundNumbers[thisRound];
        let canBeClaimed = false;
        canBeClaimed = await gameDetails.contracts.prediction.methods.Claimable(roundID, account?.address).call();
        if (!canBeClaimed)
          canBeClaimed = await gameDetails.contracts.prediction.methods.Refundable(roundID, account?.address).call();
        if (canBeClaimed)
          roundsToBeClaimed.push(roundID);
      }
      //console.log('done processing round number(not ID) ' + thisRound); // debug
      if (thisRound >= roundsProcessed + loadingScreenUpdatePeriod) {
        roundsProcessed = thisRound;
        SetRoundsProcessed(roundsProcessed);
      }
    }
    return roundsToBeClaimed;
  }


  async function ClaimAllRounds() {
    if (!claimableRounds || claimableRounds.length < 1) // nothing to do
      return;

    let simulationResult = null;
    try {
      simulationResult = await publicClient.simulateContract({
        address: GetPredictionContractAddr(gameDetails),
        abi: ABI.prediction,
        functionName: 'user_Claim',
        args: [claimableRounds],
        account: account.address,
        value: 0n,
      });
    } catch (e) {
      if (e?.shortMessage) {
        ToastErrorTx(e.shortMessage);
      }
      return;
    }

    let txHash = '';
    ShowTxPendingToast();
    try {
      txHash = await walletClient.data.writeContract(simulationResult.request);
      SetBetTxHash(txHash);
    } catch (e) {
      if (e?.shortMessage) {
        SetFormattedError(e.shortMessage);
      } else {
        SetFormattedError(String(e).slice(0, 40));
      }
      SetBetTxHash(null);
      return;
    }
  }



  function GetMaxNumberOfRoundsToQuery() {
    return maxRoundsOverride || MAX_ROUNDS_CLAIMALL_DEFAULT;
  }

  function HandleKeyPress_MaxRoundsOverride(event) {
    const enterKeyCode = 13;
    if (event.keyCode === enterKeyCode || event.which === enterKeyCode) {
      UpdateOverride();
    }
  }

  function UpdateOverride() {
    const newOverride = GetNormalizedOverrideFigure();
    if (newOverride === null) {
      alert('Your input is not a number. You need to input # of rounds to look up');
      return;
    }
    SetMaxRoundsOverride(newOverride);
    window.location.reload();
  }

  function GetNormalizedOverrideFigure() {
    const newOverride = Number(inputField_MaxRoundsOverride.current?.value);
    if (Number.isNaN(newOverride))
      return null;
    else if (newOverride < 1)
      return 1;
    else if (newOverride > MAX_ROUNDS_CLAIMALL_HARDCAP)
      return MAX_ROUNDS_CLAIMALL_HARDCAP;
    else
      return newOverride;
  }

  /* JSX methods below */

  const mNRoundsToCheck = useMemo(NRoundToCheck, [maxRoundsOverride]);
  function NRoundToCheck() {
    return (<div className='MaxRounds'>
      <p>How many rounds to check:</p>
      <label className='ThemeBox'>
        <input
          type='number' defaultValue={GetMaxNumberOfRoundsToQuery()}
          step={1} min={1} max={MAX_ROUNDS_CLAIMALL_HARDCAP} ref={inputField_MaxRoundsOverride}
          onKeyDown={HandleKeyPress_MaxRoundsOverride}
        />
      </label>
      <button className='ThemeButton Gold' onClick={UpdateOverride}>
        Re-check
      </button>
    </div>);
  }

  const mClaimingUI = useMemo(ClaimingUI,
    [
      nOfPlayedRounds, claimableRounds, historyOffset, roundsProcessedSoFar,
      gameDetails, account?.address 
    ]);
  function ClaimingUI() {
    let message = null;
    if (nOfPlayedRounds === 0)
      message = (<p>You have not played any rounds</p>);
    else if (claimableRounds === null)
      message = (<p>Loading... Processing round {roundsProcessedSoFar}/{nOfPlayedRounds || 0}</p>);
    else if (claimableRounds.length === 0)
      message = (<div>
        <p>Checked last {nOfPlayedRounds} rounds that you bet on</p>
        <p>None of them can be claimed</p>
      </div>);
    else
      message = (<div>
          <p>Checked last {nOfPlayedRounds} rounds that you bet on</p>
          <p>You can claim {claimableRounds.length} of them</p>
          <p className='Ignored'>
            {historyOffset > 0
              ? `Ignored ${historyOffset} older rounds`
              : ''}
          </p>
          <button
            className='ThemeButton Gold Claim'
            onClick={ClaimAllRounds}
          >
            Claim All!
          </button>
      </div>);
    return message;
  }


  if (!account?.address)
    return (<div className={'HistoryMain ClaimAllPage' + (isMobile ? ' Mobile' : '')}>
      <div className='HistoryButtons'>
        <BackButton />
      </div>
      <h1 className='white-centered-text'>
        Please connect your wallet first!
      </h1>
    </div>);
  else
    return (<div className={'HistoryMain ClaimAllPage' + (isMobile ? ' Mobile' : '')}>
      <div className='HistoryButtons'>
        <BackButton />
      </div>
      <div className='ClaimAllBody'>
        {mNRoundsToCheck}
        {mClaimingUI}
      </div>
    </div>);
}

export default ClaimAll;
